import React from 'react';
import { Link } from 'react-router-dom';
import ServiceSubpage, { Lead, SubHead, Included, BuildItem } from '../components/ServiceSubpage';

export default function ServiceImplementation() {
  return (
    <ServiceSubpage
      seo={{
        title: 'AI Implementation Services for Small Businesses & Nonprofits | Sagient Partners',
        description: 'Hands-on AI implementation for small businesses and nonprofits — no-code AI workflow automation, custom tool integration, and a 30-day managed pilot with human sign-off on every output. $5,000–$10,000.',
        path: '/services/ai-strategy-implementation',
      }}
      hero={{
        tier: 'Tier 2 · AI Strategy Implementation · One-time · 4-8 weeks · $5,000-$10,000',
        headline: 'Small Business and Nonprofit AI Implementation Services & Workflow Automation',
        subhead: 'From roadmap to running with hands-on AI implementation services — tool selection, no-code AI workflow automation, and a 30-day managed pilot with your team approving every output.',
        meta: 'Guided execution · Human-in-the-loop from day one',
        cta: 'Ready to Build? →',
        ctaHeading: 'Ready to Build?',
        ctaSub: 'Bring your priorities and we will turn them into working, trusted automations - without the expensive missteps.',
      }}
      schema={{
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Service',
            name: 'AI Implementation Services for Small Business and Nonprofit',
            serviceType: 'AI Consulting',
            provider: { '@type': 'Organization', name: 'Sagient Partners', url: 'https://sagientpartners.com' },
            description: 'A project-based engagement that selects tools, cleans your data foundation, and builds and pilots your first agentic automations with human-in-the-loop sign-off.',
            areaServed: { '@type': 'Country', name: 'United States' },
            audience: [
              { '@type': 'Audience', audienceType: 'Small Business' },
              { '@type': 'Audience', audienceType: 'Nonprofit' },
            ],
            offers: { '@type': 'Offer', priceCurrency: 'USD', priceRange: '$5,000-$10,000', lowPrice: '5000', highPrice: '10000' },
          },
          {
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: 'Do I need an Assessment first?', acceptedAnswer: { '@type': 'Answer', text: "It helps, but it's not required — if you already know your priorities, we can start here." } },
              { '@type': 'Question', name: 'Do you replace my staff?', acceptedAnswer: { '@type': 'Answer', text: 'No. The objective is to make your existing team more effective.' } },
              { '@type': 'Question', name: 'Is AI safe for small business customer data and privacy?', acceptedAnswer: { '@type': 'Answer', text: "Yes. We build on business-tier tools that don't train on your data, and set written guardrails from day one. For regulated data, we only use vendors that will sign the appropriate agreements (e.g. a BAA for health information)." } },
            ],
          },
        ],
      }}
      faqs={[
        { q: "Do I need an Assessment first?", a: "It helps, but it’s not required — if you already know your priorities, we can start here." },
        { q: "Do you replace my staff?", a: "No. The objective is to make your existing team more effective." },
        { q: "Is AI safe for small business customer data and privacy?", a: "Yes. We build on business-tier tools that don’t train on your data, and set written guardrails from day one. For regulated data, we only use vendors that will sign the appropriate agreements (e.g. a BAA for health information)." },
      ]}
      prevNext={[
        { to: '/services/ai-readiness-assessment', label: '← AI Readiness Assessment' },
        { to: '/services/ai-advisor', label: 'Next: AI Advisor →' },
      ]}
    >
      <section className="section" style={{ paddingTop: 32, paddingBottom: 32 }}>
        <div className="container">
          <p className="section-label">Bridging the Gap in Small Business and Nonprofit AI Adoption</p>
          <Lead>
            A roadmap is only worth what you execute. The gap between &ldquo;we should automate this&rdquo; and a working
            automation your team actually trusts is where many AI projects fall short - the wrong tool, a botched
            integration, or a team that refuses to use it. This engagement closes that gap with experienced oversight, so
            you skip the expensive missteps.
          </Lead>

          <SubHead>What &ldquo;building an agent&rdquo; actually means — no-code AI integration, not development</SubHead>
          <Lead>
            It is a configuration exercise, not a coding project. We brief each agent the way you&rsquo;d brief a new
            hire: its role, its objective, the context of your organization, and strict instructions for what it must
            never do (for example, &ldquo;never send anything automatically; save every message as a draft for
            review&rdquo;). All in plain English. The no-code AI integration tools we use let us build automated
            customer intake workflows and stitch together the software you already run — HubSpot, QuickBooks, WordPress,
            Shopify, and others — securely, without hiring developers.
          </Lead>

          <SubHead>How we work</SubHead>
          <Lead>
            We take the priorities from the <Link to="/services/ai-readiness-assessment" style={{ color: '#378ADD', fontWeight: 600 }}>Readiness Assessment</Link> (or your own plan), clean, augment if needed, and
            centralize the underlying data first, as an agent is only as good as the data it can reach. Then we write the
            guardrails down in plain language and launch each automation as a tightly scoped 30-day pilot with one
            quantitative target and a human approving every output before it goes live.
          </Lead>
        </div>
      </section>

      <section className="section section--alt" style={{ paddingTop: 32, paddingBottom: 32 }}>
        <div className="container">
          <p className="section-label">Custom AI Workflows We Implement for SMBs & Nonprofits</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, marginTop: 8 }}>
            <BuildItem name="Automated customer intake & follow-up">
              Captures and routes every inbound lead from your website, WordPress forms, or HubSpot CRM; scores and
              replies to hot leads within minutes; escalates anything sensitive.
            </BuildItem>
            <BuildItem name="Quote, proposal & onboarding">
              Drafts quotes from your price book and past winners — integrating with Shopify, your job management
              software, or existing pricing tools — then automates post-&ldquo;yes&rdquo; onboarding: welcome, forms,
              documents, and kickoff scheduling.
            </BuildItem>
            <BuildItem name="Invoice & accounts-receivable">
              Generates invoices in QuickBooks, matches incoming payments, and drafts polite, escalating reminders so
              cash flow stops dying in the follow-up.
            </BuildItem>
            <BuildItem name="For nonprofits">
              Grant-vetting (parse RFPs against your eligibility criteria), donor scoring and stewardship, volunteer
              intake through your website or WordPress forms, and board-meeting prep.
            </BuildItem>
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 32, paddingBottom: 32 }}>
        <div className="container" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 48 }}>
          <div>
            <p className="section-label">What&rsquo;s Included</p>
            <Included items={[
              'Data foundation cleanup and integration',
              'Hands-on implementation and 30 days of oversight and refinement',
              'Guardrails and human-in-the-loop sign-off built into every workflow',
              'Change management and staff enablement so adoption sticks',
            ]} />
          </div>
          <div>
            <p className="section-label">Ideal For</p>
            <Lead>
              Small businesses and nonprofits that have identified their AI priorities and want expert guidance to execute
              without costly missteps.
            </Lead>
          </div>
        </div>
      </section>
    </ServiceSubpage>
  );
}
